/**
 * Public Loan Application Page
 *
 * Form-first onboarding pattern: Borrowers land on a form and create
 * an account only when necessary (before sensitive data or e-sign).
 *
 * Flow:
 * 1. Basic Info (public - no auth required)
 * 2. Property Details (public)
 * 3. Create Account (auth required from here)
 * 4. Financial Details (sensitive - requires auth)
 * 5. E-Sign Disclosures (requires auth)
 * 6. Submission Complete
 *
 * Issue #374 - Document Templates & E-Sign Integration
 */

import AccountIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import BackIcon from '@mui/icons-material/ArrowBack';
import NextIcon from '@mui/icons-material/ArrowForward';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import CheckIcon from '@mui/icons-material/Check';
import DocumentIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import {
  alpha,
  Box,
  baseColors,
  FormControlLabel,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  Grid,
  iconSizes,
  MagicInput,
  MagicSelect,
  MoneyField,
  Radio,
  RadioGroup,
  ShimmerLoader,
  SmsOptInDisclosure,
  SplitPanel,
  SplitPanelContent,
  SplitPanelHeader,
  Stack,
  Typography,
  useI18n,
  useSmsOptInConsent,
} from '@ops-matrix/design-system-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AstorAvatar } from '../../components/loans/AstorAvatar';
import { AstorStepper } from '../../components/loans/AstorStepper';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import {
  useBusinessBranding,
  useCreateDisclosureEnvelopes,
  useDisclosureEnvelopeStatuses,
  useEsignEmbedUrl,
  useRecordReferralClick,
  useRequiredDisclosures,
  useSubmitApplication,
  useValidateReferralCode,
} from '../../lib/trpc/hooks';
import { getReferralMetadata } from '../../lib/utils/referral-tracking';

// Application form data structure
export interface LoanApplicationData {
  // Step 1: Basic Info (Public)
  firstName: string;
  lastName: string;
  email: string;
  phone: string;

  // Step 2: Property Details (Public)
  propertyType: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
  purchasePrice: string;
  loanAmount: string;
  loanPurpose: string;

  // Step 4: Financial Details (Auth Required)
  annualIncome: string;
  employmentStatus: string;
  creditScoreRange: string;
  hasOtherProperties: boolean;
  existingDebtAmount: string;

  // Tracking
  referralCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

const INITIAL_DATA: LoanApplicationData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  propertyType: '',
  propertyAddress: '',
  propertyCity: '',
  propertyState: '',
  propertyZip: '',
  purchasePrice: '',
  loanAmount: '',
  loanPurpose: '',
  annualIncome: '',
  employmentStatus: '',
  creditScoreRange: '',
  hasOtherProperties: false,
  existingDebtAmount: '',
};

const STORAGE_KEY = 'opsmatrix_loan_application_data';
const ENVELOPES_STORAGE_KEY = 'opsmatrix_loan_disclosure_envelopes';

/**
 * Envelopes persisted to localStorage between page loads.
 * Key-by-email so a browser reload with the same applicant reuses existing
 * envelopes instead of creating duplicates. Includes a timestamp so stale
 * (abandoned) envelopes are discarded after their likely expiration window.
 */
interface PersistedEnvelopes {
  signerEmail: string;
  templateIds: string[]; // Stable sorted list of template IDs we already created envelopes for
  envelopes: Record<string, { envelopeId: string; submitterId: number }>;
  createdAt: number; // Unix ms
}

interface DisclosureTemplate {
  templateId: string;
  docuSealTemplateId: number;
  name: string;
  description: string;
  category: string;
  isDefault: boolean;
}

interface DisclosureDoc {
  id: string;
  name: string;
  description: string;
  required: boolean;
  submitterId: number | null;
}

// 24 hours — well short of the default 30-day DocuSeal envelope expiration,
// but long enough to cover realistic session interruptions (lunch, meetings, reboot).
const ENVELOPES_TTL_MS = 24 * 60 * 60 * 1000;

// Step definitions
const steps = [
  { labelKey: 'consumer.loan.step.basicInfo', icon: <PersonIcon />, requiresAuth: false },
  { labelKey: 'consumer.loan.step.property', icon: <HomeIcon />, requiresAuth: false },
  { labelKey: 'consumer.loan.step.createAccount', icon: <AccountIcon />, requiresAuth: false },
  { labelKey: 'consumer.loan.step.financials', icon: <MoneyIcon />, requiresAuth: true },
  { labelKey: 'consumer.loan.step.signAndSubmit', icon: <DocumentIcon />, requiresAuth: true },
];

export default function LoanApplicationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumer, isAuthenticated, isLoading: authLoading, redirectToLogin } = useConsumerAuth();
  const { t } = useI18n();
  const { data: businessBranding } = useBusinessBranding(consumer?.businessId ?? null);
  const smsConsent = useSmsOptInConsent();

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  // Hydrate from localStorage synchronously in the initializer so the save
  // effect (which runs AFTER render) cannot clobber previously-saved data.
  // Effect-based loading would race with StrictMode's double-invocation of
  // effects and wipe the data on refresh.
  const [formData, setFormData] = useState<LoanApplicationData>(() => {
    if (typeof window === 'undefined') return INITIAL_DATA;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...INITIAL_DATA, ...(JSON.parse(saved) as Partial<LoanApplicationData>) };
      }
    } catch {
      // Fall through to initial data
    }
    return INITIAL_DATA;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // E-Sign state
  const [signedDocuments, setSignedDocuments] = useState<Set<string>>(new Set());
  const [signingDialogOpen, setSigningDialogOpen] = useState(false);
  const [currentSigningDoc, setCurrentSigningDoc] = useState<{
    id: string;
    name: string;
    submitterId: number | null;
  } | null>(null);

  // Issue #1274 — Dynamic disclosures backed by DocuSeal templates.
  // Map from template ID → envelope/submitter metadata after envelopes are created.
  // Hydrate from localStorage so a refresh at step 4 doesn't create duplicate envelopes.
  const [disclosureEnvelopes, setDisclosureEnvelopes] = useState<
    Record<string, { envelopeId: string; submitterId: number }>
  >(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = localStorage.getItem(ENVELOPES_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as PersistedEnvelopes;
      // Expire stale cache — covers abandoned sessions beyond the reasonable window.
      if (Date.now() - parsed.createdAt > ENVELOPES_TTL_MS) {
        localStorage.removeItem(ENVELOPES_STORAGE_KEY);
        return {};
      }
      return parsed.envelopes;
    } catch {
      return {};
    }
  });
  const [envelopeError, setEnvelopeError] = useState<string | null>(null);
  // Derived — any envelopes present means creation has been attempted.
  const envelopesCreated = Object.keys(disclosureEnvelopes).length > 0;

  const {
    data: requiredDisclosuresData,
    isLoading: isLoadingDisclosures,
    error: disclosuresError,
  } = useRequiredDisclosures({ enabled: isAuthenticated });
  const createDisclosureEnvelopesMutation = useCreateDisclosureEnvelopes();

  // Shape disclosures for the UI. Falls back to an empty list while loading.
  const requiredDisclosures: DisclosureDoc[] = useMemo(() => {
    const templates = (requiredDisclosuresData?.templates ?? []) as DisclosureTemplate[];
    return templates.map(
      (tpl: DisclosureTemplate): DisclosureDoc => ({
        id: tpl.templateId,
        name: tpl.name,
        description: tpl.description || t('consumer.loan.disclosureDefaultDescription'),
        required: true,
        submitterId: disclosureEnvelopes[tpl.templateId]?.submitterId ?? null,
      }),
    );
  }, [requiredDisclosuresData?.templates, disclosureEnvelopes, t]);

  // Issue #1407 — Poll envelope statuses so the "X of N signed" counter ticks
  // up after the user signs in another tab/device (or after a webhook flips
  // the envelope to `completed`). The previous flow relied solely on a
  // postMessage from the embedded DocuSeal iframe — that breaks the moment
  // signing happens outside the embed (e.g. a same-context new tab against
  // local DocuSeal CE on a different origin). Polling + refetchOnWindowFocus
  // makes the UI reflect server-side truth without losing the wizard state.
  const envelopeIdList = useMemo(
    () => Object.values(disclosureEnvelopes).map((e) => e.envelopeId),
    [disclosureEnvelopes],
  );
  const hasPendingEnvelopes = useMemo(
    () => requiredDisclosures.some((d) => d.required && !signedDocuments.has(d.id)),
    [requiredDisclosures, signedDocuments],
  );
  const { data: envelopeStatusesData } = useDisclosureEnvelopeStatuses(envelopeIdList, hasPendingEnvelopes);

  // Sync server-side envelope status into the local `signedDocuments` set.
  // The Set semantics keep the existing local-postMessage path working too —
  // duplicate adds are harmless. We only flip from unsigned → signed; the
  // server is the source of truth for completion, and a completed envelope
  // never reverts.
  useEffect(() => {
    const statuses = envelopeStatusesData?.statuses;
    if (!statuses) return;
    setSignedDocuments((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const [templateId, info] of Object.entries(disclosureEnvelopes)) {
        const status = statuses[info.envelopeId]?.status;
        // Single-signer disclosure envelopes transition through
        // sent → partially_signed → completed. Once `partially_signed`
        // appears the consumer has signed; the trailing `completed`
        // webhook from DocuSeal can lag a few seconds. Treat both as
        // "signed" so the counter updates as soon as the user finishes
        // — completion is still verified server-side via the envelope
        // record itself.
        if ((status === 'completed' || status === 'partially_signed') && !next.has(templateId)) {
          next.add(templateId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [envelopeStatusesData, disclosureEnvelopes]);

  // Fetch embed URL for current signing document
  const { data: embedUrlData, isLoading: isLoadingEmbedUrl } = useEsignEmbedUrl(currentSigningDoc?.submitterId ?? null);

  // Issue #387 - Partner Referral Flow: Hooks for application submission and click tracking
  const submitApplicationMutation = useSubmitApplication();
  const recordClickMutation = useRecordReferralClick();

  // Issue #387 - Validate referral code and get partner name for display
  const referralCodeFromUrl = searchParams.get('ref') || searchParams.get('referral') || null;
  const trackingCodeFromUrl =
    searchParams.get('tc') || searchParams.get('trackingCode') || getReferralMetadata()?.trackingCode || null;
  const { data: referralValidation } = useValidateReferralCode(referralCodeFromUrl);

  // Capture referral and UTM params from URL into formData.
  // (Saved form data is hydrated in the useState initializer above.)
  useEffect(() => {
    const referralCode = searchParams.get('ref') || searchParams.get('referral');
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');

    if (referralCode || utmSource) {
      setFormData((prev) => ({
        ...prev,
        referralCode: referralCode || prev.referralCode,
        utmSource: utmSource || prev.utmSource,
        utmMedium: utmMedium || prev.utmMedium,
        utmCampaign: utmCampaign || prev.utmCampaign,
      }));
    }
  }, [searchParams]);

  // Issue #387 - Record click for partner attribution tracking
  // Separate useEffect to avoid mutation in dependency array causing re-renders
  useEffect(() => {
    const referralCode = searchParams.get('ref') || searchParams.get('referral');
    if (!referralCode) return;

    const clickKey = `opsmatrix_click_recorded_${referralCode}`;
    if (sessionStorage.getItem(clickKey)) return;

    recordClickMutation.mutate(
      {
        trackingCode: referralCode,
        landingPage: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer || undefined,
      },
      {
        onSuccess: (data) => {
          sessionStorage.setItem(clickKey, 'true');
          // Issue #390 - Store clickId for accurate attribution during submission
          // This ensures the specific click is marked as converted, avoiding race conditions
          if (data?.clickId) {
            sessionStorage.setItem(`${clickKey}_id`, data.clickId);
          }
        },
      },
    );
    // Only run once on mount or when referral code changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('ref'), searchParams.get('referral')]);

  // Save to localStorage whenever formData changes. Safe because formData is
  // hydrated synchronously in the useState initializer — no race with a
  // load-effect overwriting it with INITIAL_DATA on mount.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Update form field
  const updateField = useCallback((field: keyof LoanApplicationData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Validate current step
  const validateStep = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    switch (activeStep) {
      case 0: // Basic Info
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        break;

      case 1: // Property
        if (!formData.propertyType) newErrors.propertyType = 'Property type is required';
        if (!formData.loanPurpose) newErrors.loanPurpose = 'Loan purpose is required';
        if (!formData.loanAmount.trim()) newErrors.loanAmount = 'Loan amount is required';
        break;

      case 2: // Create Account
        // This step just shows the auth prompt, no validation needed
        break;

      case 3: // Financials
        if (!formData.annualIncome.trim()) newErrors.annualIncome = 'Annual income is required';
        if (!formData.employmentStatus) newErrors.employmentStatus = 'Employment status is required';
        break;

      case 4: // E-Sign
        // E-sign validation happens in the component
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [activeStep, formData]);

  // Handle next step
  const handleNext = useCallback(() => {
    if (!validateStep()) return;

    // Check if next step requires auth
    const nextStep = activeStep + 1;
    if (nextStep < steps.length && steps[nextStep]?.requiresAuth && !isAuthenticated) {
      // Save current step so we can return after auth
      localStorage.setItem('opsmatrix_loan_application_step', String(nextStep));
      // Pass returnPath with step info so it's preserved through auth redirect
      // Preserve existing query params (ref, utm_*, tenant, etc.) and upsert step
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('step', String(nextStep));
      const returnUrl = `${window.location.pathname}?${searchParams.toString()}`;
      redirectToLogin(undefined, returnUrl);
      return;
    }

    setActiveStep(nextStep);
  }, [activeStep, validateStep, isAuthenticated, redirectToLogin]);

  // Handle back
  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  // Handle final submission
  const handleSubmit = useCallback(async () => {
    // Ensure disclosures have loaded — prevents submission before the list is available
    if (isLoadingDisclosures) {
      setSubmitError(t('consumer.loan.disclosuresStillLoading'));
      return;
    }

    // Validate all required documents are signed
    const requiredUnsigned = requiredDisclosures
      .filter((d) => d.required && !signedDocuments.has(d.id))
      .map((d) => d.name);

    if (requiredUnsigned.length > 0) {
      setSubmitError(t('consumer.loan.pleaseSignDocuments', { documents: requiredUnsigned.join(', ') }));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Issue #390 - Retrieve the stored clickId for accurate attribution
      const clickIdKey = formData.referralCode ? `opsmatrix_click_recorded_${formData.referralCode}_id` : null;
      const storedClickId = clickIdKey ? sessionStorage.getItem(clickIdKey) : null;

      // Issue #387 - Submit application to API with partner attribution
      await submitApplicationMutation.mutateAsync({
        // Raw form values (all fields for IntakeSubmissionSummary display).
        // Spread SMS opt-in consent into formValues so it lands in
        // IntakeSubmission.formData (and structured columns once those exist).
        formValues: { ...(formData as unknown as Record<string, unknown>), ...smsConsent.payload },

        // Basic Info
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,

        // Property Details
        propertyType: formData.propertyType,
        propertyAddress: formData.propertyAddress || undefined,
        propertyCity: formData.propertyCity || undefined,
        propertyState: formData.propertyState || undefined,
        propertyZip: formData.propertyZip || undefined,
        purchasePrice: formData.purchasePrice || undefined,
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,

        // Financial Details
        annualIncome: formData.annualIncome,
        employmentStatus: formData.employmentStatus,
        creditScoreRange: formData.creditScoreRange || undefined,
        hasOtherProperties: formData.hasOtherProperties,
        existingDebtAmount: formData.existingDebtAmount || undefined,

        // Partner Attribution (Issue #387, #390)
        referralCode: formData.referralCode,
        trackingCode: trackingCodeFromUrl || undefined,
        clickId: storedClickId || undefined, // Issue #390 - Pass specific clickId for accurate conversion tracking
        utmSource: formData.utmSource,
        utmMedium: formData.utmMedium,
        utmCampaign: formData.utmCampaign,

        // E-Sign confirmation
        signedDocuments: Array.from(signedDocuments),
      });

      // Clear saved data on success
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('opsmatrix_loan_application_step');
      localStorage.removeItem(ENVELOPES_STORAGE_KEY);

      // Navigate to success/dashboard
      navigate('/dashboard', {
        state: { applicationSubmitted: true },
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('consumer.loan.submitApplicationError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    navigate,
    signedDocuments,
    requiredDisclosures,
    formData,
    submitApplicationMutation,
    isLoadingDisclosures,
    trackingCodeFromUrl,
    smsConsent.payload,
    t,
  ]);

  // Guard to prevent the restoration useEffect from re-triggering on subsequent renders
  const hasRestoredStep = useRef(false);

  // Restore step after auth — check URL params first (survives redirect), then localStorage
  useEffect(() => {
    if (hasRestoredStep.current) return;
    if (isAuthenticated && !authLoading) {
      const urlParams = new URLSearchParams(window.location.search);
      const stepFromUrl = urlParams.get('step');
      const stepFromStorage = localStorage.getItem('opsmatrix_loan_application_step');
      const savedStep = stepFromUrl ?? stepFromStorage;
      if (savedStep) {
        const parsedStep = Number.parseInt(savedStep, 10);
        hasRestoredStep.current = true;
        if (Number.isFinite(parsedStep) && parsedStep >= 0 && parsedStep < steps.length) {
          setActiveStep(parsedStep);
        }
        localStorage.removeItem('opsmatrix_loan_application_step');
        // Clean step param from URL to avoid confusion on refresh
        if (stepFromUrl) {
          urlParams.delete('step');
          const newSearch = urlParams.toString();
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  }, [isAuthenticated, authLoading]);

  // E-Sign handlers
  const handleOpenSigningDialog = useCallback((doc: { id: string; name: string; submitterId: number | null }) => {
    setCurrentSigningDoc(doc);
    setSigningDialogOpen(true);
  }, []);

  // Issue #1274 — Create disclosure envelopes once the consumer reaches step 4
  // and template metadata is available. Uses ref to avoid duplicate calls if
  // the user navigates back-and-forth between steps.
  //
  // Idempotency (Gap 2): persisted envelopes are reused when the cached
  // signerEmail + templateIds match current state — a page refresh at step 4
  // does NOT create a second set of envelopes for the same applicant.
  const envelopeCreationInFlight = useRef(false);
  const envelopeCreationAttempted = useRef(false);
  // Capture the mutation in a ref so it doesn't trigger the useEffect
  // (react-query's useMutation returns a new object every render).
  const createMutationRef = useRef(createDisclosureEnvelopesMutation);
  createMutationRef.current = createDisclosureEnvelopesMutation;

  useEffect(() => {
    if (activeStep !== 4) return;
    if (envelopeCreationInFlight.current) return;
    // After a completed attempt (success or failure) stop re-firing. The user
    // can manually retry by changing email or templates (both invalidate below).
    if (envelopeCreationAttempted.current) return;

    const templates = (requiredDisclosuresData?.templates ?? []) as DisclosureTemplate[];
    if (templates.length === 0) return;
    if (!formData.email || !formData.firstName || !formData.lastName) return;

    const templateIds = templates.map((tpl: DisclosureTemplate) => tpl.templateId);
    const sortedTemplateIds = [...templateIds].sort();

    // If hydrated cache is valid for current state, mark attempted and skip.
    if (envelopesCreated) {
      try {
        const raw = localStorage.getItem(ENVELOPES_STORAGE_KEY);
        if (raw) {
          const cached = JSON.parse(raw) as PersistedEnvelopes;
          const sortedCachedIds = [...cached.templateIds].sort();
          const emailMatches = cached.signerEmail === (consumer?.email || formData.email);
          const templatesMatch =
            sortedCachedIds.length === sortedTemplateIds.length &&
            sortedCachedIds.every((id, i) => id === sortedTemplateIds[i]);
          if (emailMatches && templatesMatch) {
            envelopeCreationAttempted.current = true;
            return;
          }
          localStorage.removeItem(ENVELOPES_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(ENVELOPES_STORAGE_KEY);
      }
      // Cache miss or stale — fall through, but don't trip the attempted guard
      // so a single fresh attempt runs.
      setDisclosureEnvelopes({});
    }

    envelopeCreationInFlight.current = true;
    envelopeCreationAttempted.current = true;
    setEnvelopeError(null);
    // Backend requires signer.email to match the authenticated user's email
    // (esign.createDisclosureEnvelopes guard). The intake form may have a
    // different email if the user typed one address but later logged in with
    // another. Prefer the authenticated user's email when available so the
    // server-side identity check passes; fall back to formData.email only
    // for the anonymous pre-auth path (which doesn't reach this mutation
    // today, but future code may).
    const signerEmail = consumer?.email || formData.email;
    createMutationRef.current.mutate(
      {
        templateIds,
        signer: {
          email: signerEmail,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          // Phone is intentionally omitted — DocuSeal requires E.164 format
          // (+<country><digits>). Email-only signing is the default flow;
          // consumers sign via the embedded iframe after clicking Review &
          // Sign. When we later capture verified E.164 phones, we can pass
          // them here for SMS-based signing reminders.
        },
      },
      {
        onSuccess: (data) => {
          const nextMap: Record<string, { envelopeId: string; submitterId: number }> = {};
          for (const r of data.results) {
            if (r.envelopeId && r.submitterId && r.submitterId > 0) {
              nextMap[r.templateId] = { envelopeId: r.envelopeId, submitterId: r.submitterId };
            }
          }
          setDisclosureEnvelopes(nextMap);
          // Persist so a refresh at step 4 reuses these envelopes.
          if (Object.keys(nextMap).length > 0) {
            try {
              const payload: PersistedEnvelopes = {
                signerEmail,
                templateIds,
                envelopes: nextMap,
                createdAt: Date.now(),
              };
              localStorage.setItem(ENVELOPES_STORAGE_KEY, JSON.stringify(payload));
            } catch {
              // Storage quota / disabled — continue without persistence.
            }
          }
          if (data.successCount < data.totalCount) {
            const failed = data.results.filter((r) => r.error);
            setEnvelopeError(
              t('consumer.loan.disclosureEnvelopePartialFailure', {
                failed: failed.length,
                total: data.totalCount,
              }),
            );
          }
        },
        onError: (err) => {
          setEnvelopeError(err?.message || t('consumer.loan.disclosureEnvelopeError'));
        },
        onSettled: () => {
          envelopeCreationInFlight.current = false;
        },
      },
    );
  }, [
    activeStep,
    envelopesCreated,
    requiredDisclosuresData?.templates,
    formData.email,
    formData.firstName,
    formData.lastName,
    t,
  ]);

  // If the signer email or required disclosure set changes after a completed
  // attempt, reset the attempted guard so a fresh attempt runs.
  useEffect(() => {
    envelopeCreationAttempted.current = false;
  }, [formData.email, requiredDisclosuresData?.templates]);

  const handleCloseSigningDialog = useCallback(() => {
    setSigningDialogOpen(false);
    setCurrentSigningDoc(null);
  }, []);

  const handleSigningComplete = useCallback(() => {
    if (currentSigningDoc) {
      setSignedDocuments((prev) => new Set([...prev, currentSigningDoc.id]));
    }
    handleCloseSigningDialog();
  }, [currentSigningDoc, handleCloseSigningDialog]);

  // Listen for postMessage from DocuSeal iframe
  // SECURITY: Validate origin to prevent cross-origin message spoofing attacks
  useEffect(() => {
    // DocuSeal allowed origins - configured via environment or defaults to production
    const ALLOWED_ORIGINS = [
      import.meta.env.VITE_DOCUSEAL_URL || 'https://app.docuseal.co',
      'https://app.docuseal.co', // Production DocuSeal
      'https://docuseal.co',
    ];

    const handleMessage = (event: MessageEvent) => {
      // SECURITY: Validate the message origin to prevent spoofing
      if (!ALLOWED_ORIGINS.some((origin) => event.origin.startsWith(origin))) {
        // Silently ignore messages from unknown origins
        return;
      }

      // DocuSeal sends messages when signing is complete
      if (event.data?.type === 'docuseal:completed' || event.data?.status === 'completed') {
        handleSigningComplete();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleSigningComplete]);

  // Check if all required documents are signed
  const allDocumentsSigned = requiredDisclosures.filter((d) => d.required).every((d) => signedDocuments.has(d.id));

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('consumer.loan.tellUsAboutYourself')}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {t('consumer.loan.contactInfoDescription')}
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicInput
                  label={t('common.firstName')}
                  value={formData.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicInput
                  label={t('common.lastName')}
                  value={formData.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicInput
                  label={t('common.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicInput
                  label={t('common.phone')}
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('consumer.loan.propertyAndLoanDetails')}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {t('consumer.loan.propertyAndLoanDescription')}
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicSelect
                  fullWidth
                  error={!!errors.loanPurpose}
                  label={`${t('consumer.loan.loanPurpose')} *`}
                  value={formData.loanPurpose}
                  onChange={(e) => updateField('loanPurpose', e.target.value as string)}
                  options={[
                    { value: 'purchase', label: t('loan.type.purchase') },
                    { value: 'refinance', label: t('loan.type.refinance') },
                    { value: 'cash_out', label: t('loan.type.cashOut') },
                    { value: 'bridge', label: t('loan.type.bridge') },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicSelect
                  fullWidth
                  error={!!errors.propertyType}
                  label={`${t('loan.propertyType')} *`}
                  value={formData.propertyType}
                  onChange={(e) => updateField('propertyType', e.target.value as string)}
                  options={[
                    { value: 'single_family', label: t('loan.propertyType.singleFamily') },
                    { value: 'multi_family', label: t('loan.propertyType.multiFamily') },
                    { value: 'commercial', label: t('loan.propertyType.commercial') },
                    { value: 'mixed_use', label: t('loan.propertyType.mixedUse') },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MoneyField
                  label={t('consumer.loan.loanAmount')}
                  value={formData.loanAmount}
                  onChange={(next) => updateField('loanAmount', next)}
                  error={!!errors.loanAmount}
                  helperText={errors.loanAmount}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MoneyField
                  label={t('consumer.loan.purchasePrice')}
                  value={formData.purchasePrice}
                  onChange={(next) => updateField('purchasePrice', next)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <MagicInput
                  label={t('consumer.loan.propertyAddress')}
                  value={formData.propertyAddress}
                  onChange={(e) => updateField('propertyAddress', e.target.value)}
                  placeholder={t('consumer.loan.streetAddress')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <MagicInput
                  label={t('consumer.loan.city')}
                  value={formData.propertyCity}
                  onChange={(e) => updateField('propertyCity', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <MagicInput
                  label={t('consumer.loan.state')}
                  value={formData.propertyState}
                  onChange={(e) => updateField('propertyState', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <MagicInput
                  label={t('consumer.loan.zipCode')}
                  value={formData.propertyZip}
                  onChange={(e) => updateField('propertyZip', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <AccountIcon sx={{ fontSize: iconSizes['4xl'], color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('consumer.loan.createYourAccount')}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {t('consumer.loan.createAccountDescription')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <GlassButton variant="gradient" onClick={handleNext}>
                {t('consumer.loan.createAccount')}
              </GlassButton>
              <GlassButton variant="outlined" onClick={() => redirectToLogin()}>
                {t('consumer.loan.signIn')}
              </GlassButton>
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
              {t('consumer.loan.infoSecure')}
            </Typography>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('consumer.loan.financialInformation')}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {t('consumer.loan.financialDescription')}
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MoneyField
                  label={t('consumer.loan.annualIncome')}
                  value={formData.annualIncome}
                  onChange={(next) => updateField('annualIncome', next)}
                  error={!!errors.annualIncome}
                  helperText={errors.annualIncome}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicSelect
                  fullWidth
                  error={!!errors.employmentStatus}
                  label={`${t('consumer.loan.employmentStatus')} *`}
                  value={formData.employmentStatus}
                  onChange={(e) => updateField('employmentStatus', e.target.value as string)}
                  options={[
                    { value: 'employed', label: t('employment.status.employed') },
                    { value: 'self_employed', label: t('employment.status.selfEmployed') },
                    { value: 'retired', label: t('employment.status.retired') },
                    { value: 'investor', label: t('employment.status.investor') },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MagicSelect
                  fullWidth
                  label={t('consumer.loan.creditScoreRange')}
                  value={formData.creditScoreRange}
                  onChange={(e) => updateField('creditScoreRange', e.target.value as string)}
                  options={[
                    { value: 'excellent', label: t('consumer.loan.creditScore.excellent') },
                    { value: 'good', label: t('consumer.loan.creditScore.good') },
                    { value: 'fair', label: t('consumer.loan.creditScore.fair') },
                    { value: 'below_fair', label: t('consumer.loan.creditScore.belowFair') },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MoneyField
                  label={t('consumer.loan.existingDebt')}
                  value={formData.existingDebtAmount}
                  onChange={(next) => updateField('existingDebtAmount', next)}
                  helperText={t('consumer.loan.existingDebtHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('consumer.loan.otherProperties')}
                </Typography>
                <RadioGroup
                  row
                  value={formData.hasOtherProperties ? 'yes' : 'no'}
                  onChange={(e) => updateField('hasOtherProperties', e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} />
                  <FormControlLabel value="no" control={<Radio />} label={t('common.no')} />
                </RadioGroup>
              </Grid>
            </Grid>
          </Box>
        );

      case 4: {
        const creatingEnvelopes = createDisclosureEnvelopesMutation.isPending;
        const totalRequired = requiredDisclosures.filter((d) => d.required).length;
        const progressPercent = totalRequired > 0 ? (signedDocuments.size / totalRequired) * 100 : 0;

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('consumer.loan.reviewSignDisclosures')}
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              {t('consumer.loan.reviewSignDescription')}
            </Typography>

            {isLoadingDisclosures || creatingEnvelopes ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  py: 6,
                }}
              >
                <ShimmerLoader variant="circular" width={40} height={40} />
                <Typography color="textSecondary">{t('consumer.loan.preparingDisclosures')}</Typography>
              </Box>
            ) : disclosuresError ? (
              <GlassAlert severity="error" sx={{ mb: 2 }}>
                {t('consumer.loan.disclosureLoadError')}
              </GlassAlert>
            ) : requiredDisclosures.length === 0 ? (
              <GlassAlert severity="info" sx={{ mb: 2 }}>
                {t('consumer.loan.noDisclosuresConfigured')}
              </GlassAlert>
            ) : (
              <>
                {envelopeError && (
                  <GlassAlert severity="warning" sx={{ mb: 2 }} onClose={() => setEnvelopeError(null)}>
                    {envelopeError}
                  </GlassAlert>
                )}

                {/* E-Sign Documents List */}
                <Box sx={{ mb: 3 }}>
                  {requiredDisclosures.map((doc) => {
                    const isSigned = signedDocuments.has(doc.id);
                    const isReady = doc.submitterId !== null && doc.submitterId > 0;
                    return (
                      <GlassCard
                        key={doc.id}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: isSigned ? 2 : 1,
                          borderColor: isSigned ? 'success.main' : 'divider',
                          bgcolor: isSigned ? 'success.main' : 'transparent',
                          ...(isSigned && { bgcolor: (theme) => alpha(theme.palette.success.main, 0.08) }),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {isSigned ? <CheckIcon color="success" /> : <DocumentIcon color="primary" />}
                            <Box>
                              <Typography variant="subtitle2">
                                {doc.name}
                                {doc.required && !isSigned && (
                                  <GlassChip
                                    label={t('consumer.loan.required')}
                                    size="small"
                                    color="error"
                                    sx={{ ml: 1, height: 20 }}
                                  />
                                )}
                                {isSigned && (
                                  <GlassChip
                                    label={t('consumer.loan.signed')}
                                    size="small"
                                    color="success"
                                    sx={{ ml: 1, height: 20 }}
                                  />
                                )}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {doc.description}
                              </Typography>
                            </Box>
                          </Box>
                          {isSigned ? (
                            <GlassTooltip title={t('consumer.loan.alreadySigned')}>
                              <GlassIconButton size="small" disabled>
                                <CheckIcon color="success" />
                              </GlassIconButton>
                            </GlassTooltip>
                          ) : !isReady ? (
                            // Envelope created but submitter ID hasn't backfilled yet.
                            // Render a visible-but-disabled "Preparing…" button so the
                            // user sees the affordance immediately; the live click
                            // target (the next branch) mounts once submitterId > 0.
                            // No data-testid here — Playwright's getByRole(name=/review & sign/)
                            // and getByTestId('disclosure-review-sign-btn') both target
                            // the live button below, so the spec waits naturally.
                            <GlassTooltip title={t('consumer.loan.preparingDisclosures')}>
                              <span>
                                <GlassButton
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditIcon />}
                                  loading
                                  disabled
                                  aria-label={t('consumer.loan.preparing')}
                                >
                                  {t('consumer.loan.preparing')}
                                </GlassButton>
                              </span>
                            </GlassTooltip>
                          ) : (
                            <GlassButton
                              data-testid="disclosure-review-sign-btn"
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              aria-label={t('consumer.loan.reviewAndSign')}
                              onClick={() =>
                                handleOpenSigningDialog({
                                  id: doc.id,
                                  name: doc.name,
                                  submitterId: doc.submitterId,
                                })
                              }
                            >
                              {t('consumer.loan.reviewAndSign')}
                            </GlassButton>
                          )}
                        </Box>
                      </GlassCard>
                    );
                  })}
                </Box>

                {/* Progress indicator */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {t('consumer.loan.documentsSignedProgress', {
                      signed: signedDocuments.size,
                      total: totalRequired,
                    })}
                  </Typography>
                  <Box
                    sx={{
                      height: 4,
                      bgcolor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${progressPercent}%`,
                        bgcolor: allDocumentsSigned ? 'success.main' : 'primary.main',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>

                {allDocumentsSigned ? (
                  <GlassAlert severity="success" icon={<CheckIcon />}>
                    {t('consumer.loan.allDocumentsSigned')}
                  </GlassAlert>
                ) : (
                  <GlassAlert severity="info">{t('consumer.loan.signDocumentsNotice')}</GlassAlert>
                )}
              </>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2 }}>
        {/* Header — spec borrower-portal style: title row + sub */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} sx={{ mb: 0.75, alignItems: 'center' }}>
            <AddIcon sx={{ color: 'text.secondary', fontSize: iconSizes.lg }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('consumer.newLoan.title', { defaultValue: 'New loan' })}
            </Typography>
          </Stack>
          <Typography color="textSecondary" variant="body2">
            {t('consumer.newLoan.subtitle', {
              defaultValue: 'Astor pre-fills what it knows — you confirm and add the rest.',
            })}
          </Typography>

          {/* Issue #387 - Show partner referral banner with validation */}
          {formData.referralCode && referralValidation?.valid && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <GlassChip
                label={
                  referralValidation.referrerFirstName && referralValidation.referrerCompanyName
                    ? t('referral.referredByFromCompany', {
                        firstName: referralValidation.referrerFirstName,
                        companyName: referralValidation.referrerCompanyName,
                      })
                    : t('referral.referredByName', { name: referralValidation.partnerName || formData.referralCode })
                }
                color="primary"
                variant="filled"
              />
              {referralValidation.partnerCompanyName && (
                <GlassChip
                  label={t('referral.referredToCompany', { companyName: referralValidation.partnerCompanyName })}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
          {formData.referralCode && referralValidation && !referralValidation.valid && (
            <GlassChip
              label={'expired' in referralValidation ? t('referral.expired') : t('referral.invalid')}
              color="default"
              variant="outlined"
              sx={{ mt: 2, opacity: 0.7 }}
            />
          )}
        </Box>

        {/* Spec — 6-step indicator (AstorStepper, compact) */}
        <GlassCard sx={{ p: 2, mb: 2 }}>
          <AstorStepper
            stages={steps.map((s) => t(s.labelKey))}
            current={activeStep}
            size="compact"
            inProgressLabel={t('consumer.newLoan.inProgress', { defaultValue: 'In progress' })}
          />
        </GlassCard>

        {/* Spec — Astor auto-fill banner */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${baseColors.teal[200]}`,
            background: `linear-gradient(135deg, ${baseColors.teal[50]} 0%, transparent 70%)`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
          }}
        >
          <AstorAvatar size={28} pulse />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {t('consumer.newLoan.astor.prefillBanner', {
                defaultValue: "I'll pre-fill what I know as you go — confirm or correct anything that has changed.",
              })}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('consumer.newLoan.astor.prefillSub', {
                defaultValue: 'You stay in control of every field before anything is submitted.',
              })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <GlassChip
              size="small"
              label={t('consumer.newLoan.astor.confidence', { defaultValue: 'conf 92%' })}
              sx={{ bgcolor: baseColors.teal[100], color: baseColors.teal[700] }}
            />
            <GlassButton variant="text" size="small">
              {t('consumer.newLoan.astor.seeSources', { defaultValue: 'See sources' })}
            </GlassButton>
          </Stack>
        </Box>

        {/* Error Alert */}
        {submitError && (
          <GlassAlert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </GlassAlert>
        )}

        {/* Step Content */}
        <GlassCard sx={{ mb: 3 }}>{renderStepContent()}</GlassCard>

        {/* TCPA / A2P SMS opt-in — rendered above submit on the LAST step,
            because Daniel's email and the TCR rejection both require the
            disclosure + checkboxes to sit directly above the submit affordance. */}
        {activeStep === steps.length - 1 && (
          <SmsOptInDisclosure companyName={businessBranding?.name ?? undefined} sx={{ mb: 3 }} {...smsConsent.bind} />
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <GlassButton variant="outlined" startIcon={<BackIcon />} onClick={handleBack} disabled={activeStep === 0}>
            {t('common.back')}
          </GlassButton>

          {activeStep < steps.length - 1 ? (
            <GlassButton variant="gradient" endIcon={<NextIcon />} onClick={handleNext}>
              {t('common.continue')}
            </GlassButton>
          ) : (
            <GlassButton variant="gradient" endIcon={<CheckIcon />} onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? t('common.submitting') : t('consumer.loan.submitApplication')}
            </GlassButton>
          )}
        </Box>

        {/* Trust Badges */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            🔒 Your information is encrypted and secure
          </Typography>
        </Box>
      </Box>

      {/* DocuSeal Signing Panel - SplitPanel wrapping wizard content per CLAUDE.md rule #14 */}
      <SplitPanel
        panelOpen={signingDialogOpen}
        onPanelClose={handleCloseSigningDialog}
        panel={
          <>
            <SplitPanelHeader
              title={currentSigningDoc?.name ?? t('consumer.loan.signDocument')}
              subtitle={t('consumer.loan.reviewDocumentSubtitle')}
              onClose={handleCloseSigningDialog}
            />
            <SplitPanelContent padding={0}>
              <Box sx={{ overflow: 'auto', height: '100%' }}>
                {isLoadingEmbedUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      gap: 2,
                    }}
                  >
                    <ShimmerLoader variant="circular" width={40} height={40} />
                    <Typography color="textSecondary">{t('consumer.loan.loadingDocument')}</Typography>
                  </Box>
                ) : embedUrlData?.url ? (
                  <iframe
                    src={embedUrlData.url}
                    title={`Sign ${currentSigningDoc?.name}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="clipboard-write"
                    // SECURITY: Minimal sandbox permissions for DocuSeal signing
                    // - allow-scripts: Required for DocuSeal JS
                    // - allow-same-origin: Required for DocuSeal to function
                    // - allow-forms: Required for form submission
                    // NOTE: allow-popups removed - not required by DocuSeal signing flow
                    sandbox="allow-scripts allow-same-origin allow-forms"
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      gap: 2,
                      p: 4,
                    }}
                  >
                    <GlassAlert severity="warning">{t('consumer.loan.unableToLoad')}</GlassAlert>
                    <GlassButton variant="outlined" onClick={handleCloseSigningDialog}>
                      {t('common.close')}
                    </GlassButton>
                  </Box>
                )}
              </Box>
            </SplitPanelContent>
          </>
        }
      >
        <Box />
      </SplitPanel>
    </Box>
  );
}
